/** @jsx jsx */
import { constants as pureMultipleChoiceExerciseConstants } from '@datacamp/pure-multiple-choice-exercise-core';
import { constants as videoExerciseConstants } from '@datacamp/video-exercise-core';
import { Link } from '@datacamp/waffles/link';
import { Paragraph } from '@datacamp/waffles/paragraph';
import { jsx } from '@emotion/react';
import startCase from 'lodash/startCase';
import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { I18N_PLACEHOLDER } from '../i18n';
import { useDispatch, useSelector } from '../interfaces/State';
import * as actions from '../redux/actions';
import { selectExercise } from '../redux/selectors';
import ReportModal from '../shared-components/ReportModal';

type Props = {
  onExit: () => void;
  submitRequestState: any;
};

const IssueReporter: React.FC<Props> = ({ onExit, submitRequestState }) => {
  const dispatch = useDispatch();
  const exerciseType = useSelector(selectExercise).get('type');
  const onSubmit = useCallback(
    (settings: any) => dispatch(actions.epicSubmitIssue(settings)),
    [dispatch],
  );
  const { t } = useTranslation(['translation', 'common']);

  const baseCategories = [
    {
      value: t('IssueReporter.notAcceptedRightAnswerValue'),
      key: 'not-accepted-right-answer',
    },
    {
      value: t('IssueReporter.feedbackNotHelpfulValue'),
      key: 'feedback-not-helpful',
    },
    {
      value: t('IssueReporter.hintNotHelpfulValue'),
      key: 'hint-not-helpful',
    },
    { value: t('IssueReporter.typoExValue'), key: 'typo-ex' },
  ];

  const categories: Record<string, any[]> = {
    MultipleChoiceExercise: [
      ...baseCategories,
      {
        value: t('IssueReporter.sessionExpiredValue'),
        key: 'session-expired',
      },
      { value: startCase(t('other', { ns: 'common' })), key: 'other' },
    ],
    [pureMultipleChoiceExerciseConstants.exercises
      .PURE_MULTIPLE_CHOICE_EXERCISE]:
      pureMultipleChoiceExerciseConstants.issueCategories,
    default: [
      ...baseCategories,
      {
        value: t('IssueReporter.answerNotCorrectValue'),
        key: 'answer-not-correct',
      },
      {
        value: t('IssueReporter.sessionExpiredValue'),
        key: 'session-expired',
      },
      { value: startCase(t('other', { ns: 'common' })), key: 'other' },
    ],
    [videoExerciseConstants.exercises.VIDEO_EXERCISE]:
      videoExerciseConstants.issueCategories,
  };
  const category = categories[exerciseType] || categories.default;

  return (
    <ReportModal
      submitRequestState={submitRequestState}
      successMessage={
        <Paragraph>
          <Trans
            t={t}
            i18nKey="IssueReporter.successMessage"
            components={{
              supportLink: (
                <Link
                  href="https://support.datacamp.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  css={{
                    borderBottom: 0,
                  }}
                >
                  {I18N_PLACEHOLDER}
                </Link>
              ),
            }}
          />
        </Paragraph>
      }
      modalTitle={t('IssueReporter.modalTitle')}
      onExit={onExit}
      onSubmit={onSubmit}
      categories={category}
    />
  );
};

export default IssueReporter;
